/**
 * Which plan cluster an unclustered page belongs in, as the review surface and the agent export say it.
 * The verdict is computed on the server (`backend/src/utils/unclustered-cluster-fit.ts`) from the
 * clusters its same-folder sibling pages already sit in; this file only words it.
 */

export interface UnclusteredClusterCandidate {
  /** The plan cluster's label. */
  cluster: string;
  /** Paths of the related pages in this cluster, closest first (at most a few). */
  siblings: string[];
  /** How many related pages in the folder sit in this cluster (may exceed `siblings.length`). */
  siblingCount: number;
}

export interface UnclusteredClusterFamily {
  /** The family's word in the slug ("contract" for contract templates). */
  word: string;
  /** Paths of the family's other unclustered pages (at most a few). */
  members: string[];
  /** How many other family pages are in no cluster (may exceed `members.length`). */
  memberCount: number;
  /** How many family pages are in a cluster. */
  clusteredCount: number;
}

export type UnclusteredClusterFit =
  /** One cluster holds clearly most of the related pages. */
  | ({ verdict: 'named' } & UnclusteredClusterCandidate)
  /**
   * Related pages exist but split across clusters, or only one is related: the nearest, best first.
   * `family`: most of the page's format family is in no cluster either, so the family is a group
   * still waiting for one.
   */
  | { verdict: 'none_fits'; nearest: UnclusteredClusterCandidate[]; family?: UnclusteredClusterFamily }
  /** No clustered page in the same folder shares the page's subject words. */
  | { verdict: 'no_siblings' };

const listPaths = (paths: string[], total: number): string => {
  const more = total - paths.length;
  if (more > 0) return `${paths.join(', ')} and ${more} more`;
  if (paths.length <= 1) return paths.join('');
  return `${paths.slice(0, -1).join(', ')} and ${paths[paths.length - 1]}`;
};

const pages = (n: number): string => (n === 1 ? '1 related page' : `${n} related pages`);

/** The action line for an unclustered page with traffic, once its cluster fit is known. */
export function unclusteredClusterAction(fit: UnclusteredClusterFit): string {
  if (fit.verdict === 'named') return `Assign it to the "${fit.cluster}" cluster`;
  if (fit.verdict === 'none_fits' && fit.family) {
    const nearest = fit.nearest[0];
    return nearest
      ? `Give the "${fit.family.word}" pages a cluster: a new one for the group, or the nearest existing one, "${nearest.cluster}"`
      : `Give the "${fit.family.word}" pages a new cluster: none of the plan's clusters is near them`;
  }
  return 'Choose a cluster for it: none of the plan\'s clusters clearly fits';
}

/** Why: which pages the verdict rests on, in one sentence a client can read. */
export function unclusteredClusterFitSentence(fit: UnclusteredClusterFit): string {
  if (fit.verdict === 'named') {
    return `${pages(fit.siblingCount)} in the same folder ${fit.siblingCount === 1 ? 'is' : 'are'} in "${fit.cluster}": ${listPaths(fit.siblings, fit.siblingCount)}.`;
  }
  if (fit.verdict === 'no_siblings') {
    return 'No page in the same folder that shares its subject is in a cluster yet.';
  }
  const [a, b] = fit.nearest;
  if (fit.family) {
    const f = fit.family;
    const group =
      `${f.memberCount + 1} of the folder's "${f.word}" pages are in no cluster: this one, ` +
      `${listPaths(f.members, f.memberCount)}` +
      (f.clusteredCount > 0 ? ` (${f.clusteredCount === 1 ? '1 other is' : `${f.clusteredCount} others are`} in a cluster)` : '') +
      '. They form a group with no cluster yet.';
    if (!a) return group;
    if (!b) return `${group} The nearest existing cluster is "${a.cluster}" (${listPaths(a.siblings, a.siblingCount)}).`;
    return (
      `${group} The nearest existing clusters are "${a.cluster}" (${listPaths(a.siblings, a.siblingCount)}) ` +
      `and "${b.cluster}" (${listPaths(b.siblings, b.siblingCount)}).`
    );
  }
  if (!a) return 'No page in the same folder that shares its subject is in a cluster yet.';
  if (!b) {
    return `The only related page in the same folder, ${listPaths(a.siblings, a.siblingCount)}, is in "${a.cluster}": too little to go on.`;
  }
  return (
    `Related pages in the same folder are split. The nearest are "${a.cluster}" ` +
    `(${listPaths(a.siblings, a.siblingCount)}) and "${b.cluster}" (${listPaths(b.siblings, b.siblingCount)}).`
  );
}
